/**
 * ATTE PoE switch + extender catalog.
 *
 * ATTE is a Polish manufacturer specialising in CCTV-focused PoE
 * switches that double as PoE EXTENDERS — they can be powered FROM
 * a PoE uplink (instead of via a separate AC adapter) and fan out
 * 2-4 PoE-out ports downstream. That makes them the go-to choice
 * when an installer needs to extend a single PoE run past the 100 m
 * UTP limit (the switch repeats the run while powering itself off
 * the same cable).
 *
 * Two product families:
 *   • APT — desktop / compact (typically 51×51 mm, ABOX-mountable)
 *   • IPB / IP — outdoor IP55/IP66 enclosure for camera-pole installs
 *
 * Every entry verified against atte.pl product page or dipolnet.com
 * (authorised distributor citing the ATTE datasheet verbatim).
 *
 * Compiled: 2026-05-15, release/v1.6.7-23k-catalog.
 */
import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

export const ATTE_POE_SWITCHES: EquipmentCatalogEntry[] = [
  // ═══════════════════════════════════════════════════════════════════════
  // APT compact desktop — Gigabit
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://www.atte.pl/en_US/APT-3-50
  //      APT-3-50: 3 ports total (1 PoE-in / 2 PoE-out), 1000 Mbps
  //      all ports, IEEE 802.3af + 802.3at + Passive PoE input,
  //      802.3af/at output, ~13 W (af) / 25 W (at) available on the
  //      extender side, 30 W max total when powered from UTP. Compact
  //      51×51×21 mm with universal mounting base (ABOX-compatible),
  //      -25..+65 °C operating range. Surge protection on all ports.
  {
    id: 'atte-apt-3-50',
    manufacturer: 'ATTE',
    model: 'APT-3-50',
    category: 'poe-switch',
    tagline:
      'APT-3-50 — 3-port Gigabit PoE extender (1 IN + 2 OUT), 802.3at, ABOX-mountable',
    verified: true,
    datasheetUrl: 'https://www.atte.pl/en_US/APT-3-50',
    specs: {
      totalPorts: 3,
      poePorts: 2, // PoE OUT count
      poeBudgetW: 30, // total when fed via PoE-in UTP
      poeStandard: '802.3at',
      poePerPortW: 25, // 802.3at max
      uplinks: '1× GbE (PoE-in capable)',
      management: 'unmanaged',
      formFactor: 'desktop',
      fanless: true,
      operatingTempC: '-25..+65',
    },
  },

  // SRC: https://www.atte.pl/en_US/APT-5-50
  // SRC: https://www.dipolnet.com/poe_switch_extender_atte_apt-5-50_5_ports_802_3bt_10-100-1000_mbps_N29825.htm
  //      APT-5-50: 5 ports total (1 PoE-in LAN1 / 4 PoE-out LAN2-5),
  //      Gigabit on all ports, IEEE 802.3af/at/bt90 + Passive PoE
  //      input, 802.3af/at/bt60 output, up to 90 W total budget when
  //      powered from UTP, ~1.4 A individual per-port limit, 0.3 W
  //      idle, 44-58 V input, -25..+65 °C, surge protection.
  //      Universal mounting base (ABOX + RACK adapter compatible).
  {
    id: 'atte-apt-5-50',
    manufacturer: 'ATTE',
    model: 'APT-5-50',
    category: 'poe-switch',
    tagline:
      'APT-5-50 — 5-port Gigabit PoE extender (1 IN + 4 OUT), 802.3bt 90W, ABOX-mountable',
    verified: true,
    datasheetUrl: 'https://www.atte.pl/en_US/APT-5-50',
    specs: {
      totalPorts: 5,
      poePorts: 4,
      poeBudgetW: 90,
      poeStandard: '802.3bt-Type3', // bt60 output side
      poePerPortW: 60, // bt60 per spec
      uplinks: '1× GbE (PoE-in capable, accepts 802.3bt90 / Passive)',
      management: 'unmanaged',
      formFactor: 'desktop',
      fanless: true,
      operatingTempC: '-25..+65',
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // IPB outdoor enclosure — Fast Ethernet, ready for pole-mount installs
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://www.dipolnet.com/poe_switch_atte_ipb-5-10a-s4_5xpoe_1xuplink_802_3af-at_passive_90_w_ip55_N29815.htm
  // SRC: https://www.cyberteam.pl/en/produkt/9210_atte-switch-6-portowy-5xpoe-1xuplink-ipb-5-10a-s4-w-obudowie-zewnetrznej
  //      IPB-5-10A-S4: 6 ports (5× PoE + 1× uplink), 10/100 Mbps Fast
  //      Ethernet, IEEE 802.3af/at + Passive PoE, 90 W total budget,
  //      up to 40 W per port (passive supports higher than 802.3at),
  //      integrated 55 V / 90 W power supply, IP55 outdoor housing
  //      designed for direct pole / wall mount alongside cameras.
  //      Unmanaged, configured out-of-the-box.
  {
    id: 'atte-ipb-5-10a-s4',
    manufacturer: 'ATTE',
    model: 'IPB-5-10A-S4',
    category: 'poe-switch',
    tagline:
      'IPB-5-10A-S4 — 6-port FE PoE switch in IP55 outdoor enclosure, 90 W, integrated PSU',
    verified: true,
    datasheetUrl:
      'https://www.dipolnet.com/poe_switch_atte_ipb-5-10a-s4_5xpoe_1xuplink_802_3af-at_passive_90_w_ip55_N29815.htm',
    specs: {
      totalPorts: 6,
      poePorts: 5,
      poeBudgetW: 90,
      poeStandard: '802.3at',
      poePerPortW: 40, // up to 40 W per port with passive mode
      uplinks: '1× 10/100 Mbps RJ45',
      management: 'unmanaged',
      formFactor: 'desktop', // outdoor enclosure (pole / wall) — closest schema value
      fanless: true,
    },
  },
];
